import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import TopNav from "./TopNav";
import SchedulePage from "./SchedulePage";
import GuestsPage from "./GuestsPage";
import GuestView from "./GuestView";
import GuideView from "./GuideView";
import FamilyView from "./FamilyView";
import ActivitiesPage from "./ActivitiesPage";
import GuestChoicesPage from "./GuestChoicesPage";
import FamilyChoicesPage from "./FamilyChoicesPage";
import SolverPage from "./SolverPage";
import FamiliesPage from "./FamiliesPage";
import ReportsPage from "./ReportsPage";
import GuidesPage from "./GuidesPage";
import { UnsavedChangesProvider } from "./UnsavedChangesContext";

export default function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [authStatus, setAuthStatus] = useState({ loading: false, error: "" });

  useEffect(() => {
    const stored = localStorage.getItem("brushcreek_admin");
    if (stored === "true") setIsAdmin(true);
  }, []);

  async function handleLogin(code) {
    const trimmed = (code || "").trim();
    if (!trimmed) return false;

    setAuthStatus({ loading: true, error: "" });
    try {
      const res = await fetch("/api/admin/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: trimmed }),
      });
      if (!res.ok) {
        setAuthStatus({ loading: false, error: "Invalid admin code" });
        setIsAdmin(false);
        localStorage.removeItem("brushcreek_admin");
        return false;
      }
      setIsAdmin(true);
      localStorage.setItem("brushcreek_admin", "true");
      setAuthStatus({ loading: false, error: "" });
      return true;
    } catch {
      setAuthStatus({ loading: false, error: "Unable to verify code" });
      setIsAdmin(false);
      localStorage.removeItem("brushcreek_admin");
      return false;
    }
  }

  function handleLogout() {
    setIsAdmin(false);
    setAuthStatus({ loading: false, error: "" });
    localStorage.removeItem("brushcreek_admin");
  }

  return (
    <BrowserRouter>
      <UnsavedChangesProvider>
        <AppShell
          isAdmin={isAdmin}
          authStatus={authStatus}
          onLogin={handleLogin}
          onLogout={handleLogout}
        />
      </UnsavedChangesProvider>
    </BrowserRouter>
  );
}

function AppShell({ isAdmin, authStatus, onLogin, onLogout }) {
  const location = useLocation();
  const isGuestShare = location.pathname.startsWith("/guest/");
  const isGuideShare = location.pathname.startsWith("/guide/");
  const isFamilyShare = location.pathname.startsWith("/family/");
  const isShareRoute = isGuestShare || isGuideShare || isFamilyShare;

  if (isShareRoute) {
    return (
      <Routes>
        <Route path="/guest/:guestId" element={<GuestView />} />
        <Route path="/guest/:guestId/choices" element={<GuestChoicesPage />} />
        <Route path="/guide/:guideId" element={<GuideView />} />
        <Route path="/family/:familyId" element={<FamilyView />} />
        <Route path="/family/:familyId/choices" element={<FamilyChoicesPage />} />
      </Routes>
    );
  }

  if (!isAdmin) {
    return (
      <LoginPage authStatus={authStatus} onLogin={onLogin} />
    );
  }

  const containerStyle = {
    maxWidth: "100%",
    margin: "0 auto",
    padding: 20,
  };

  return (
    <div style={containerStyle}>
      <TopNav onLogout={onLogout} />

      <Routes>
        <Route path="/" element={<SchedulePage isAdmin={isAdmin} />} />
        <Route path="/guests" element={<GuestsPage isAdmin={isAdmin} />} />
        <Route path="/activities" element={<ActivitiesPage isAdmin={isAdmin} />} />
        <Route path="/guides" element={<GuidesPage isAdmin={isAdmin} />} />
        <Route path="/families" element={<FamiliesPage />} />
        <Route path="/optimizer" element={<SolverPage isAdmin={isAdmin} />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/guest/:guestId" element={<GuestView />} />
        <Route path="/guest/:guestId/choices" element={<GuestChoicesPage />} />
        <Route path="/guide/:guideId" element={<GuideView />} />
        <Route path="/family/:familyId" element={<FamilyView />} />
        <Route path="/family/:familyId/choices" element={<FamilyChoicesPage />} />
      </Routes>
    </div>
  );
}

function LoginPage({ authStatus, onLogin }) {
  const [code, setCode] = useState("");

  async function submitLogin() {
    const ok = await onLogin(code);
    if (ok) setCode("");
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        backgroundImage: 'url("/backgroundlogin.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 360,
          border: "1px solid rgba(255, 255, 255, 0.6)",
          borderRadius: 12,
          padding: 16,
          background: "rgba(255, 255, 255, 0.9)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 18 }}>Admin Login</div>
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
          Enter your admin code to access scheduling tools.
        </div>
        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <input
            type="password"
            placeholder="Admin code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitLogin();
            }}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc" }}
          />
          <button onClick={submitLogin} disabled={authStatus?.loading || !code.trim()}>
            {authStatus?.loading ? "Checking…" : "Log in"}
          </button>
          {authStatus?.error ? (
            <div style={{ fontSize: 12, color: "crimson" }}>{authStatus.error}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
